import { inject, Injectable } from '@angular/core';
import { FieldReferenceModel, FieldReferenceState, FieldReferenceType } from '@app/models/incident.model';
import { IncidentDetailsPageStore } from './incident-details-page.store';
import { MediaReferenceMetadataModel } from '@app/models/media-reference-metadata.model';
import { ArticleReferenceModel } from '@app/models/article-reference.model';
import { capitalizeRimsPath } from '@app/utils/rims/rims.utils';

@Injectable()
export class FieldReferenceService {
  private readonly incidentsPageStore = inject(IncidentDetailsPageStore);

  // Add

  public addIncidentFieldReference(name: string, sourceIncidentId: number): void {
    this.replaceFieldReference(name, sourceIncidentId, 'Incident');
  }

  public addArticleFieldReference(articleReference: ArticleReferenceModel): void {
    this.replaceFieldReference(`ArticleReferences/${articleReference.id}`, articleReference.id, 'ArticleReference');
    this.addArticleToArticleReferencesToMergeCollection([articleReference]);
  }

  public addArticleToArticleReferencesToMergeCollection(articleReferences: ArticleReferenceModel[]) {
    const existing = this.incidentsPageStore.articleReferencesToMerge() ?? [];
    const existingIds = new Set(existing.map((ar) => ar.id));
    const newReferences = articleReferences.filter((ar) => !existingIds.has(ar.id));

    if (newReferences.length > 0) {
      this.incidentsPageStore.articleReferencesToMerge.set([...existing, ...newReferences]);
    }
  }

  public addMediaFieldReference(mediaReference: MediaReferenceMetadataModel): void {
    this.replaceFieldReference(`MediaReferences/${mediaReference.id}`, mediaReference.id, 'MediaReference');
    this.addMediaToMediaReferencesToMergeCollection([mediaReference]);
  }

  public addMediaToMediaReferencesToMergeCollection(mediaReferences: MediaReferenceMetadataModel[]) {
    const existing = this.incidentsPageStore.mediaReferencesToMerge() ?? [];
    const existingIds = new Set(existing.map((mr) => mr.id));
    const newReferences = mediaReferences.filter((mr) => !existingIds.has(mr.id));

    if (newReferences.length > 0) {
      this.incidentsPageStore.mediaReferencesToMerge.set([...existing, ...newReferences]);
    }
  }

  public addTechnicalStepFieldReference(stepId: string): void {
    const fieldReferences = this.getFieldReferences();
    if (!fieldReferences) {
      return;
    }

    const existingReferenceIndex = fieldReferences.findIndex(
      (fieldReference) => fieldReference.name === stepId && fieldReference.state === 'Removed',
    );
    if (existingReferenceIndex !== -1) {
      fieldReferences.splice(existingReferenceIndex, 1);

      // Mark as edited
      this.incidentsPageStore.isFieldReferencesEdited = true;
    }
  }

  public addTechnicalStepFieldFieldReference(stepId: string, propertyPath: string, technicalStepId: number): void {
    // Backend-stored names use PascalCase segments, while the propertyPath here is camelCase.
    this.replaceFieldReference(capitalizeRimsPath(`${stepId}/${propertyPath}`), technicalStepId, 'TechnicalStep');
  }

  public addTechnicalStepSetItemFieldReference(stepId: string, setId: string, technicalStepId: number): void {
    // setId is a guid and must never be capitalized.
    this.replaceFieldReference(`${capitalizeRimsPath(stepId)}/Set/${setId}`, technicalStepId, 'TechnicalStep');
  }

  // Remove

  public uncheckIncidentFieldReference(name: string): void {
    this.markFieldReferenceAs(name, 'Manual');
  }

  public uncheckTechnicalStepFieldReference(stepId: string, propertyPath: string): void {
    this.markFieldReferenceAs(capitalizeRimsPath(`${stepId}/${propertyPath}`), 'Manual');
  }

  public uncheckTechnicalStepSetItemFieldReference(stepId: string, setId: string): void {
    // setId is a guid and must never be capitalized.
    this.markFieldReferenceAs(`${capitalizeRimsPath(stepId)}/Set/${setId}`, 'Manual');
  }

  public removeArticleFieldReference(articleReference: ArticleReferenceModel): void {
    this.markFieldReferenceAs(`ArticleReferences/${articleReference.id}`, 'Removed');
    this.incidentsPageStore.articleReferencesToMerge.set(
      this.incidentsPageStore.articleReferencesToMerge()?.filter((ar) => ar.id !== articleReference.id) ?? null,
    );
  }

  public removeMediaFieldReference(mediaReference: MediaReferenceMetadataModel): void {
    this.markFieldReferenceAs(`MediaReferences/${mediaReference.id}`, 'Removed');

    const mediaReferencesToMerge = this.incidentsPageStore.mediaReferencesToMerge();
    if (mediaReferencesToMerge) {
      const index = mediaReferencesToMerge.findIndex((mr) => mr.id === mediaReference.id);
      if (index !== -1) {
        mediaReferencesToMerge.splice(index, 1);
      }
    }

    this.incidentsPageStore.mediaReferencesToMerge.set(
      this.incidentsPageStore.mediaReferencesToMerge()?.filter((mr) => mr.id !== mediaReference.id) ?? null,
    );
  }

  // Delete the found reference from the collection. Adding a new step is of no relevance for the backend. Therefore we don't add anything in the collection.
  public removeTechnicalStepFieldReference(stepId: string, technicalStepId: number): void {
    const fieldReferences = this.getFieldReferences();
    if (!fieldReferences) {
      return;
    }

    // Remove all references starting with the stepId
    const remainingFieldReferences = fieldReferences.filter(
      (fieldReference) => !fieldReference.name.startsWith(stepId),
    );
    fieldReferences.splice(0, fieldReferences.length, ...remainingFieldReferences);

    // Add one reference pointing that the whole step is removed
    fieldReferences.push({
      name: stepId,
      sourceId: technicalStepId,
      type: 'TechnicalStep',
      state: 'Removed',
    });

    // Mark as edited
    this.incidentsPageStore.isFieldReferencesEdited = true;
  }

  public removeTechnicalStepSetItemFieldReference(stepId: string, setId: string): void {
    // setId is a guid and must never be capitalized.
    this.markFieldReferenceAs(`${capitalizeRimsPath(stepId)}/Set/${setId}`, 'Removed');
  }

  // Search

  public takeAllArticleReferenceFieldReferences(): FieldReferenceModel[] | undefined {
    return this.incidentsPageStore.incident()?.fieldReferences?.filter((fr) => fr.type === 'ArticleReference');
  }

  public takeAllMediaReferenceFieldReferences(): FieldReferenceModel[] | undefined {
    return this.incidentsPageStore.incident()?.fieldReferences?.filter((fr) => fr.type === 'MediaReference');
  }

  private replaceFieldReference(name: string, sourceId: number, type: FieldReferenceType): void {
    const fieldReferences = this.getFieldReferences();
    if (!fieldReferences) {
      return;
    }

    // Mark an existing match as removed
    const existingReference = fieldReferences.find((fieldReference) => fieldReference.name === name);
    if (existingReference) {
      existingReference.state = 'Sourced';
      existingReference.sourceId = sourceId;
    } else {
      // Add a new reference as sourced
      fieldReferences.push({ name, sourceId, type, state: 'Sourced' });
    }

    // Mark as edited
    this.incidentsPageStore.isFieldReferencesEdited = true;
  }

  private markFieldReferenceAs(name: string, state: FieldReferenceState): void {
    const fieldReferences = this.getFieldReferences();
    if (!fieldReferences) {
      return;
    }

    // Mark an existing match as removed
    const existingReference = fieldReferences.find((fieldReference) => fieldReference.name === name);
    if (!existingReference) {
      return;
    }

    existingReference.state = state;

    // Mark as edited
    this.incidentsPageStore.isFieldReferencesEdited = true;
  }

  private getFieldReferences(): FieldReferenceModel[] | null {
    const incident = this.incidentsPageStore.incident();
    if (!incident) {
      return null;
    }

    incident.fieldReferences ??= [];
    return incident.fieldReferences;
  }
}
